import { colorFromUserId } from '@pin/components/utils/colors';
import { displayUserName } from '@pin/components/utils/userName';
import { renderEmojis } from '@pin/emotes/emoji';
import { renderEmotes } from '@pin/emotes/emote';
import type { Overlay } from '@pin/schemas/overlay';
import gsap from 'gsap';
import type { SuperMessage } from './super-message';
import './super-message-chat.css';
import { preloadImages } from './utils/dom';
import { collectRefs, html } from './utils/html';

/**
 * A chat message as a sports super, in the bottom-left corner on the
 * subtitle line. Modelled on the supers of nora-render-sport2026: the box
 * wipes in from the left with a lead slab ahead of it, and the message slides
 * into place behind it. The box itself is black with the name in the user's
 * colour, as in the chat the viewers see, and the marker takes the accent
 * green of the poll tag.
 */
const params = {
  /** Emotes and emoji must be decoded before the take starts. */
  imagePreloadTimeout: 4000,
  durationIn: 0.45,
  durationOut: 0.45,
  durationLead: 0.4,
  delayLead: 0.1,
  durationMessage: 0.6,
  durationMarker: 0.8,
  durationMarkerSlide: 0.4,
  delayMarker: 0.14,
  /** Distance the message travels while it slides in and out. */
  slideIn: 150,
  slideOut: 100,
  contentSlideOut: 32,
  markerSlideIn: -80,
  easeSlab: 'expo',
  easeLead: 'power3',
  easeContent: 'power1',
  easeMessage: 'expo',
  easeMarker: 'power3',
  easeMarkerSlide: 'power2',
  easeOut: 'power3',
  /**
   * The lead and the marker sweep from the client's green-800 to the accent
   * green, as the lead of the poll tag does.
   */
  leadColorStart: '#016630',
  leadColorEnd: '#2dd17d',
};

/** Clipped so nothing of the element is visible, collapsed at its left edge. */
const clipToLeft = 'inset(0% 100% 0% 0%)';
/** Clipped so nothing of the element is visible, collapsed at its right edge. */
const clipToRight = 'inset(0% 0% 0% 100%)';
const unclipped = 'inset(0% 0% 0% 0%)';

export class SuperMessageChat implements SuperMessage {
  private readonly refs = collectRefs(
    html`<div data-ref="frame" class="super-message-chat">
      <div class="safe-area">
        <div data-ref="box" class="box">
          <div data-ref="mainSlab" class="main-slab"></div>
          <div data-ref="leadSlab" class="lead-slab"></div>
          <div data-ref="marker" class="marker"></div>
          <div data-ref="content" class="content">
            <div data-ref="message" class="message"></div>
          </div>
        </div>
      </div>
    </div>`,
    {
      frame: HTMLDivElement,
      box: HTMLDivElement,
      mainSlab: HTMLDivElement,
      leadSlab: HTMLDivElement,
      marker: HTMLDivElement,
      content: HTMLDivElement,
      message: HTMLDivElement,
    }
  );
  private pendingOverlay: Overlay | null | undefined = undefined;
  private isAnimating = false;

  /** @param fontSize Size of the message text in px. */
  constructor(fontSize: number) {
    this.refs.frame.style.setProperty('--message-font-size', `${fontSize}px`);

    gsap.set(this.refs.box, this.rest.box);
    gsap.set(this.refs.mainSlab, this.rest.mainSlab);
    gsap.set(this.refs.leadSlab, this.rest.leadSlab);
    gsap.set(this.refs.marker, this.rest.marker);
    gsap.set(this.refs.content, this.rest.content);
    gsap.set(this.refs.message, this.rest.message);
  }

  /**
   * Rest state with nothing visible, from which `take()` starts. The box is
   * hidden with opacity and visibility rather than autoAlpha, so the takeout
   * can be scrubbed back in GSAP DevTools (see PollNotification).
   */
  private readonly rest = {
    box: { opacity: 0, visibility: 'hidden', clipPath: unclipped, x: 0 },
    mainSlab: { clipPath: clipToLeft },
    leadSlab: { clipPath: clipToRight, backgroundColor: params.leadColorEnd },
    marker: {
      clipPath: clipToLeft,
      x: 0,
      backgroundColor: params.leadColorEnd,
    },
    content: { clipPath: clipToLeft, x: 0 },
    message: { x: 0, opacity: 1 },
  };

  public get element(): HTMLDivElement {
    return this.refs.frame;
  }

  public async animate(overlay: Overlay | null): Promise<void> {
    this.pendingOverlay = overlay;

    if (this.isAnimating) return;

    this.isAnimating = true;

    while (this.pendingOverlay !== undefined) {
      const targetOverlay = this.pendingOverlay;
      this.pendingOverlay = undefined;

      if (!targetOverlay) {
        await this.takeout()?.then();
        continue;
      }

      // The new content, images included, is ready before the take starts.
      // A visible message is taken out meanwhile.
      const [, content] = await Promise.all([
        this.takeout()?.then(),
        this.createMessageContent(targetOverlay),
      ]);
      this.refs.message.replaceChildren(content);
      await this.take().then();
    }

    this.isAnimating = false;
  }

  public async prepare(overlay: Overlay): Promise<void> {
    const content = await this.createMessageContent(overlay);
    this.refs.message.replaceChildren(content);
  }

  /**
   * Render the name and the text with its emotes and emoji, as the chat
   * does, and wait for the images to be decoded. Images that fail to load in
   * time are dropped, so the take never shows a broken or half-loaded image.
   */
  private async createMessageContent(overlay: Overlay): Promise<Element> {
    const content = html`<span>
      <span
        class="username"
        style=${{ color: colorFromUserId(overlay.user.id) }}
      >
        ${displayUserName(overlay.user.name)}
      </span>
      : <span data-ref="text">${overlay.text}</span>
    </span>`;
    const { text } = collectRefs(content, { text: HTMLSpanElement });

    renderEmotes(import.meta.env.VITE_EMOTES_BASE_URL, text);
    renderEmojis(import.meta.env.VITE_EMOTES_BASE_URL, text);

    await preloadImages(text, params.imagePreloadTimeout);

    return content;
  }

  private isVisible(): boolean {
    return gsap.getProperty(this.refs.box, 'opacity') === 1;
  }

  public take() {
    const timeline = gsap.timeline();

    return (
      timeline
        .set(this.refs.box, { autoAlpha: 1, clipPath: unclipped, x: 0 })
        // The black box grows out of the left edge.
        .fromTo(
          this.refs.mainSlab,
          { clipPath: clipToLeft },
          {
            clipPath: unclipped,
            duration: params.durationIn,
            ease: params.easeSlab,
          },
          0
        )
        // The lead starts covering the whole box and retreats to the right,
        // cooling from the dark green to the accent on the way, so it looks
        // like it is sweeping ahead of the slab.
        .fromTo(
          this.refs.leadSlab,
          { clipPath: unclipped, backgroundColor: params.leadColorStart },
          {
            clipPath: clipToRight,
            backgroundColor: params.leadColorEnd,
            duration: params.durationLead,
            ease: params.easeLead,
          },
          params.delayLead
        )
        .fromTo(
          this.refs.content,
          { clipPath: clipToLeft, x: 0 },
          {
            clipPath: unclipped,
            duration: params.durationIn,
            ease: params.easeContent,
          },
          0
        )
        .fromTo(
          this.refs.message,
          { opacity: 0, x: params.slideIn },
          {
            opacity: 1,
            x: 0,
            duration: params.durationMessage,
            ease: params.easeMessage,
          },
          0
        )
        // The marker slides in from behind the left edge and settles on the
        // accent green.
        .fromTo(
          this.refs.marker,
          { clipPath: clipToLeft, backgroundColor: params.leadColorStart },
          {
            clipPath: unclipped,
            backgroundColor: params.leadColorEnd,
            duration: params.durationMarker,
            ease: params.easeMarker,
          },
          params.delayMarker
        )
        .fromTo(
          this.refs.marker,
          { x: params.markerSlideIn },
          {
            x: 0,
            duration: params.durationMarkerSlide,
            ease: params.easeMarkerSlide,
          },
          params.delayMarker
        )
    );
  }

  public takeout() {
    if (!this.isVisible()) return;

    const timeline = gsap.timeline();

    return (
      timeline
        .to(
          this.refs.message,
          {
            x: params.slideOut,
            opacity: 0,
            duration: params.durationOut,
            ease: params.easeOut,
          },
          0
        )
        .to(
          this.refs.content,
          {
            x: params.contentSlideOut,
            clipPath: clipToRight,
            duration: params.durationOut,
            ease: params.easeOut,
          },
          0
        )
        .to(
          this.refs.box,
          {
            clipPath: clipToRight,
            duration: params.durationOut,
            ease: params.easeOut,
          },
          0
        )
        .to(this.refs.box, { opacity: 0, duration: params.durationOut }, 0.1)
        // Back to rest as zero-duration tweens, so the timeline can be
        // scrubbed backwards in GSAP DevTools.
        .set(this.refs.box, this.rest.box)
        .set(this.refs.mainSlab, this.rest.mainSlab)
        .set(this.refs.leadSlab, this.rest.leadSlab)
        .set(this.refs.marker, this.rest.marker)
        .set(this.refs.content, this.rest.content)
        .set(this.refs.message, this.rest.message)
    );
  }
}
